import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { apiService } from '../services/api';

interface Report {
  id: number;
  title: string;
  query: string;
  report_length: string;
  detail_level: string;
  sources: string[];
  themes: string[];
  created_at: string;
}

const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportContent, setReportContent] = useState<string>('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Report generation form state
  const [query, setQuery] = useState('');
  const [reportLength, setReportLength] = useState('full_review');
  const [detailLevel, setDetailLevel] = useState('research_grade');
  const [language, setLanguage] = useState('en');

  const fetchReports = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.get('/reports');
      if (response.success && response.data) {
        setReports(response.data.reports || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch reports');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleViewReport = async (report: Report) => {
    setSelectedReport(report);
    setIsViewDialogOpen(true);

    try {
      const response = await apiService.get(`/reports/${report.id}`);
      if (response.success && response.data) {
        setReportContent(response.data.content);
      }
    } catch (err) {
      console.error('Failed to fetch report content:', err);
      setReportContent('Failed to load report content.');
    }
  };

  const handleGenerateReport = async () => {
    if (!query.trim()) return;

    setIsGenerating(true);
    try {
      const response = await apiService.post('/reports', {
        query: query.trim(),
        report_length: reportLength,
        detail_level: detailLevel,
        language: language,
      });

      if (response.success) {
        // Refresh the reports list
        await fetchReports();
        setIsGenerateDialogOpen(false);
        setQuery('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = () => {
    if (!reportContent) return;

    // Create a blob and download
    const blob = new Blob([reportContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport?.title || 'report'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getReportLengthLabel = (length: string) => {
    const labels: Record<string, string> = {
      brief: 'Brief (~500 words)',
      standard: 'Standard (~2,000 words)',
      comprehensive: 'Comprehensive (~5,000 words)',
      full_review: 'Full Review (~10,000 words)',
    };
    return labels[length] || length;
  };

  const getDetailLevelLabel = (level: string) => {
    const labels: Record<string, string> = {
      summary: 'Summary',
      standard: 'Standard',
      deep_analysis: 'Deep Analysis',
      research_grade: 'Research Grade',
    };
    return labels[level] || level;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Research Reports
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => setIsGenerateDialogOpen(true)}
          startIcon={<DescriptionIcon />}
        >
          Generate Report
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No reports generated yet
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Generate comprehensive literature reviews on any research topic
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => setIsGenerateDialogOpen(true)}
          >
            Generate Your First Report
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {reports.map((report) => (
            <Grid size={{ xs: 12, md: 6 }} key={report.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {report.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Query: {report.query}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Chip
                      label={getReportLengthLabel(report.report_length)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={getDetailLevelLabel(report.detail_level)}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(report.created_at)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewReport(report)}
                  >
                    View
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* View Report Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedReport?.title}</DialogTitle>
        <DialogContent dividers>
          <Box
            className="markdown-content"
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: 600,
              },
              '& p': { mb: 1.5, lineHeight: 1.8 },
              '& ul, & ol': { ml: 3, mb: 1.5 },
              '& li': { mb: 0.5 },
              '& code': {
                bgcolor: 'grey.100',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& pre': {
                bgcolor: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                mb: 1.5,
              },
              '& blockquote': {
                borderLeft: 4,
                borderColor: 'grey.300',
                pl: 2,
                fontStyle: 'italic',
                color: 'text.secondary',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: reportContent
                .replace(/\n/g, '<br />')
                .replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>')
                .replace(/`([^`]+)`/g, '<code>$1</code>')
                .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                .replace(/^# (.+)$/gm, '<h1>$1</h1>')
                .replace(/^- (.+)$/gm, '<li>$1</li>')
                .replace(/(<li>.+<\/li>\n?)+/g, '<ul>$&</ul>'),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          <Button onClick={handleDownloadReport} startIcon={<DownloadIcon />}>
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={isGenerateDialogOpen} onClose={() => setIsGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Literature Report</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Research Topic"
              placeholder="e.g., CRISPR gene editing in cancer therapy"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              multiline
              rows={2}
              disabled={isGenerating}
            />

            <FormControl fullWidth>
              <InputLabel>Report Length</InputLabel>
              <Select
                value={reportLength}
                onChange={(e) => setReportLength(e.target.value)}
                label="Report Length"
                disabled={isGenerating}
              >
                <MenuItem value="brief">Brief (~500 words)</MenuItem>
                <MenuItem value="standard">Standard (~2,000 words)</MenuItem>
                <MenuItem value="comprehensive">Comprehensive (~5,000 words)</MenuItem>
                <MenuItem value="full_review">Full Review (~10,000 words)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Detail Level</InputLabel>
              <Select
                value={detailLevel}
                onChange={(e) => setDetailLevel(e.target.value)}
                label="Detail Level"
                disabled={isGenerating}
              >
                <MenuItem value="summary">Summary</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="deep_analysis">Deep Analysis</MenuItem>
                <MenuItem value="research_grade">Research Grade</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                label="Language"
                disabled={isGenerating}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="zh">Chinese</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="fr">French</MenuItem>
                <MenuItem value="de">German</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsGenerateDialogOpen(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateReport}
            variant="contained"
            disabled={isGenerating || !query.trim()}
            startIcon={isGenerating ? <CircularProgress size={20} /> : <DescriptionIcon />}
          >
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsPage;
